import type { Product } from '@/types'
import type { LineSelection } from '@/lib/lineModifiers'

/**
 * The counter cart line — the single declaration (F-MOD-N2).
 *
 * This used to be declared twice: `CartItem` in CounterInterface.tsx and a
 * structurally identical `UnsentCartItem` in rail/UnsentItemsSection.tsx. Both
 * now import from here. Two declarations of the cart line is precisely the
 * thing that drifts silently once lines stop being one-per-product.
 *
 * ── Why there are TWO identity fields ──────────────────────────────────────
 *
 * `lineId` is opaque and permanent: minted when the line is created, never
 * recomputed. It is the React key, the ref key, and the target of every
 * decrement / note / edit / remove. It survives edits.
 *
 * `mergeKey` is derived and changes whenever the line's content changes. It is
 * used for EXACTLY ONE decision: when a product is tapped, does it bump an
 * existing line or push a new one.
 *
 * They must not be the same field. If `mergeKey` were also the React key, then
 * typing a note would change the line's identity mid-render — invalidating
 * `cartRowRefs`, remounting the row, and killing focus inside the very popover
 * the operator is typing in.
 *
 * ── The property that makes this refactor a no-op ──────────────────────────
 *
 * A line with no selections and no note produces the merge key
 * `"<productId>##"`. So for every product that has no modifier groups — which
 * is every product today — tapping merges exactly as it did when the code
 * keyed on `product.id` directly. That is what lets the identity refactor ship
 * ahead of the modifier feature with "the POS behaves exactly as before" as
 * its acceptance criterion.
 */
export interface CartItem {
  /** Stable per-line identity. Never recomputed; never derived from content. */
  lineId: string
  /** Derived from content. Only ever consulted to merge a tap into a line. */
  mergeKey: string
  product: Product
  quantity: number
  special_instructions?: string
  /** Always an array — `[]` for a plain product, never undefined. */
  selections: LineSelection[]
}

/**
 * A cart line with its resolved unit price attached.
 *
 * The price is DERIVED at render time from the live menu, never stored on the
 * line and never persisted. This follows the precedent in useCounterDraft's
 * `restoreDraftItems`, which deliberately rebuilds every line from the fresh
 * product so a stale price cannot be resurrected from a draft written before
 * the owner edited the menu.
 */
export interface PricedCartItem extends CartItem {
  unitPrice: number
  /**
   * The chosen options, formatted for display ("Large", "Extra Shot x2").
   *
   * Derived at render time from the live catalogue, exactly like unitPrice, so
   * a renamed option shows its new name on a staged line and nothing stale is
   * ever persisted. The SOLD record snapshots the name instead — that one must
   * not change after the fact.
   */
  modifierLabels: string[]
}

/** Per-line kitchen note cap. Was declared separately in two components. */
export const ITEM_NOTE_MAX = 140
/** Order-level note cap. */
export const ORDER_NOTE_MAX = 280

/**
 * Normalise a note for merge purposes: trimmed, case-folded, inner whitespace
 * collapsed. "No Sugar" and "no  sugar" are the same instruction to a cook, so
 * they must not produce two kitchen lines.
 */
function normaliseNote(note: string | undefined): string {
  return (note ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * Build the deterministic merge key for a line's content.
 *
 * Shape: `productId#opt1xN+opt2xM#note`, with the option parts sorted as
 * formatted strings so ordering of the operator's taps cannot produce two keys
 * for the same basket of choices.
 *
 * Deliberate exclusions:
 *
 *   • `group_id` — `option_id` already implies its group, so including both
 *     doubles the surface with no gain.
 *   • price — a key that embedded price would split lines the moment an owner
 *     edited a price mid-shift, stranding half the cart on the old figure.
 *
 * Deliberate INCLUSION: the note. This is a real behaviour change and worth
 * knowing about. Today `addToCart` merges on product id before any note
 * exists, so tapping a noted item a second time silently makes the kitchen
 * produce 2x with that note. With the note in the key, the second tap opens a
 * clean unnoted line, and editing a note folds the line back into a matching
 * one.
 */
export function cartLineMergeKey(
  productId: string,
  selections: ReadonlyArray<Pick<LineSelection, 'option_id' | 'quantity'>>,
  note?: string,
): string {
  const byOption = new Map<string, number>()
  for (const s of selections) {
    if (s.quantity < 1) continue
    byOption.set(s.option_id, (byOption.get(s.option_id) ?? 0) + s.quantity)
  }
  const optionPart = Array.from(byOption, ([id, qty]) => `${id}x${qty}`)
    .sort()
    .join('+')
  return `${productId}#${optionPart}#${normaliseNote(note)}`
}

/** Mint a fresh opaque line id. */
export function newLineId(): string {
  // crypto.randomUUID is available in Electron, every supported browser, and
  // jsdom under vitest. The counter has no other id source that is collision
  // free across a shift.
  return crypto.randomUUID()
}

/** Build a new cart line, computing its merge key from its content. */
export function makeCartLine(
  product: Product,
  quantity: number,
  selections: LineSelection[] = [],
  special_instructions?: string,
): CartItem {
  return {
    lineId: newLineId(),
    mergeKey: cartLineMergeKey(product.id, selections, special_instructions),
    product,
    quantity,
    selections,
    ...(special_instructions ? { special_instructions } : {}),
  }
}

/**
 * Recompute `mergeKey` after a line's content changed, then fold any collision.
 *
 * When editing a note (or, later, a selection) makes a line identical to
 * another one already in the cart, the two must become a single line of summed
 * quantity. Without the fold the operator sees two identical rows and the
 * kitchen gets two identical KOT lines.
 *
 * The SURVIVOR is the earlier line, keeping its `lineId` and its position, so
 * the rail does not reorder under the operator's hand mid-edit.
 */
export function foldCartCollisions(cart: CartItem[]): CartItem[] {
  const out: CartItem[] = []
  const indexByKey = new Map<string, number>()
  for (const line of cart) {
    const at = indexByKey.get(line.mergeKey)
    if (at === undefined) {
      indexByKey.set(line.mergeKey, out.length)
      out.push(line)
      continue
    }
    out[at] = { ...out[at], quantity: out[at].quantity + line.quantity }
  }
  return out
}
